import { useCallback, useEffect } from 'react';

import { CampaignPage } from '#crowdfunding/pages/Campaigns/CampaignPage/CampaignPage.js';
import { ParticipantPostRegistrationSuccess } from '#crowdfunding/pages/Campaigns/Participants/ParticipantPostRegistrationSuccess';
import { ParticipantSetup } from '#crowdfunding/pages/Campaigns/Participants/ParticipantSetup';
import { StudentCenter } from '#crowdfunding/pages/Campaigns/StudentCenter/StudentCenter.js';
import { EventPage } from '#crowdfunding/pages/Event/EventPage.js';
import { useShowConsumerCampaignSettingsById } from '@givebacks/api/store';
import { EventDatePage } from '@givebacks/shop';
import { useShowCauseW_Selector } from 'core/Api';
import { useSetAtom } from 'jotai';
import { Navigate, Outlet, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { LoadingOverlay, notifications } from 'ui';

import { storeCauseIdAtom, useCrowdfundingProvider } from '../../../Context';
import { ParticipantPage } from '../../pages/Campaigns/Participants/Participant';
import { ParticipantsPage } from '../../pages/Campaigns/Participants/Participants';
import { RegistrationPage } from '../../pages/Campaigns/Participants/Registration';
import { TeamPage } from '../../pages/Campaigns/Teams/Team';
import { TeamsPage } from '../../pages/Campaigns/Teams/Teams';

const ParticipantRoutes = () => {
  return (
    <>
      <Routes>
        <Route path="post-registration" element={<ParticipantPostRegistrationSuccess />} />
        <Route path="setup" element={<ParticipantSetup />} />
      </Routes>
      <Routes>
        <Route path="*" element={<ParticipantPage />} />
      </Routes>
    </>
  );
};

const StudentCenterRoutes = () => {
  return (
    <>
      <Routes>
        <Route path="participants" element={<ParticipantsPage />} />

        <Route path="participants/:participant_uuid/log-activity" element={<ParticipantsPage />} />
        <Route path="teams" element={<TeamsPage />} />

        <Route path="teams/:team_uuid/register" element={<RegistrationPage />} />
        <Route path="register" element={<RegistrationPage />} />
      </Routes>
    </>
  );
};

const SubCampaignRoutes = () => {
  return (
    <>
      <Routes>
        <Route
          path="participant-center/participants/:participant_uuid/*"
          element={<ParticipantRoutes />}
        />
        <Route path="participant-center/teams/:team_uuid" element={<TeamPage />} />
        <Route path="participant-center/*" element={<StudentCenter />} />
      </Routes>

      <Routes>
        <Route path="participant-center/*" element={<StudentCenterRoutes />} />
      </Routes>
    </>
  );
};

const CampaignRoutes = () => {
  return (
    <Routes>
      <Route path="*" element={<SubCampaignRoutes />} />
    </Routes>
  );
};

// This is a wrapper that will mount the campaign id to the selectedCampaignAtom
const CampaignMounterWrapper = () => {
  const setStoreCauseId = useSetAtom(storeCauseIdAtom);
  const { campaign_uuid, uuid } = useParams();

  const { setCrowdfundingCampaign, campaignId, causeId } = useCrowdfundingProvider();
  const { data } = useShowCauseW_Selector({ uuid });

  useEffect(() => {
    if (campaign_uuid) {
      if (campaignId !== campaign_uuid) {
        setCrowdfundingCampaign(campaign_uuid);
      }
    }
  }, [campaignId, campaign_uuid, setCrowdfundingCampaign]);

  useEffect(() => {
    if (data?.uuid) {
      if (data.uuid !== causeId) {
        setStoreCauseId(data.uuid);
      }
    }
  }, [data, causeId, setStoreCauseId]);

  return <Outlet />;
};

const CampaignsReleaseLayout = ({ basePath }: { basePath: string }) => {
  const { causeId, campaignId } = useCrowdfundingProvider();

  const navigate = useNavigate();
  const { data: cause, isPending: isLoadingCause } = useShowCauseW_Selector({
    uuid: causeId,
  });
  const {
    data,
    isPending: isLoadingCampaignSettings,
    isEnabled,
    isSuccess,
  } = useShowConsumerCampaignSettingsById({
    causeId,
    campaignId,
  });

  const showNotification = useCallback(() => {
    let message = 'This Campaign is not available.';

    switch (true) {
      case data?.isCampaignDraft:
        message = 'This campaign is not available yet. Please check back later.';
        break;
      case data?.isCampaignFinished:
        message = 'This campaign has finished.';
        break;

      default:
        break;
    }
    notifications.show({
      variant: 'error',
      message: message,
    });
  }, [data?.isCampaignDraft, data?.isCampaignFinished]);

  if (isLoadingCause || isLoadingCampaignSettings) {
    return <LoadingOverlay visible={true} />;
  }

  // When do we want to redirect?:
  // 1. If the cause is not a beta campaign
  // 2. Campaign state
  //   a. If the campaign query is enabled, and we know the campaign is not published, redirect

  /** If the campaign data is returned, we can assume the user has access to it */
  const hasAccess = isSuccess;

  const isCampaignNotAccessible = Boolean(
    !cause?.release?.campaigns?.beta || (!data?.isCampaignPublished && isEnabled)
  );
  if (hasAccess === false) {
    if (isCampaignNotAccessible) {
      showNotification();
      navigate(basePath);
    }
  }

  return <Outlet />;
};

const EventRoutes = ({
  EventMap,
}: {
  EventMap?: React.JSXElementConstructor<Record<string, unknown>>;
}) => {
  const { causeId, campaignId: eventId } = useCrowdfundingProvider();
  return (
    <>
      <Routes>
        <Route path="*" element={<EventPage EventMap={EventMap} />} />
      </Routes>
      <Routes>
        <Route
          path="event-date/:event_date_uuid"
          element={<EventDatePage causeId={causeId} eventId={eventId} />}
        />
      </Routes>
    </>
  );
};

const CampaignsRoutes = ({
  EventMap,
  isEventRoute,
  basePath,
}: {
  EventMap?: React.JSXElementConstructor<Record<string, unknown>>;
  isEventRoute?: boolean;
  /** Base Path is used to re-route any uncaught routes at the base level */
  basePath: string;
}) => {
  return (
    <Routes>
      <Route element={<CampaignMounterWrapper />}>
        <Route element={<CampaignsReleaseLayout basePath={basePath} />}>
          {isEventRoute ? (
            <>
              <Route path=":campaign_uuid/credit" element={<CampaignPage />} />
              <Route path=":campaign_uuid/*" element={<EventRoutes EventMap={EventMap} />} />
            </>
          ) : (
            <>
              <Route path=":campaign_uuid" element={<CampaignPage />} />
              <Route path=":campaign_uuid/credit" element={<CampaignPage />} />
              <Route path=":campaign_uuid/*" element={<CampaignRoutes />} />
            </>
          )}

          <Route path="*" element={<Navigate to={`${basePath}`} replace />} />
        </Route>
      </Route>
    </Routes>
  );
};

export { CampaignsRoutes };
